<!-- Plan progress card (plan-cards-spec §Plan progress): 320px live card — header counter,
     eight phase rows (identity icon + description + state rail) with one engine-pass sub-row
     per seen pass nested under the run_engine row, fixed two-line detail slot, pre-engine
     planned-task list (placeholder until the planning tasks list arrives) and the grouped
     execution board. Layout/spacing ride Bootstrap utilities + M3 system classes; the SCSS
     holds only the fixed card chrome, the phase-row grid, the detail slot height and the spin
     keyframes. -->
<div class="plan-progress-card mat-border-subtle mat-corner-lg d-flex flex-column gap-2">
  <!-- 1. Header: title + right "n/m tasks done" counter (hidden while no tasks are known) -->
  <div class="d-flex align-items-center justify-content-between gap-2">
    <span class="mat-font-title-sm">Plan progress</span>
    @if (progressText(); as progress) {
      <span class="mat-font-label-md mat-text-on-surface-variant">{{ progress }}</span>
    }
  </div>

  <!-- 2. Phase rows: identity icon / description / right-rail state; row tooltip carries the
       display label + state word. Idle rows dim entirely; a completed phase (persisted or
       live) never renders gray again (component ruling). The run_engine row nests one
       sub-row per engine pass seen (Discovery/Execution/Analysis, neutral "Engine" fallback
       for unknown-or-null passes) — passes never seen simply don't render. -->
  @for (row of phaseRows(); track row.phase) {
    <div
      class="plan-progress-card__phase-row"
      [class.plan-progress-card__phase-row--idle]="row.idle"
      [matTooltip]="row.tooltip"
    >
      <mat-icon class="mat-icon-md" aria-hidden="true">{{ row.icon }}</mat-icon>
      <span class="mat-font-body-md">{{ row.description }}</span>
      <mat-icon class="mat-icon-sm" [class]="row.railClass" aria-hidden="true">{{ row.railIcon }}</mat-icon>
    </div>
    @if (row.phase === "run_engine") {
      @for (pass of enginePassRows(); track pass.key) {
        <div class="plan-progress-card__phase-row ms-3" [matTooltip]="pass.tooltip">
          <mat-icon class="mat-icon-sm mat-text-on-surface-variant" aria-hidden="true">subdirectory_arrow_right</mat-icon>
          <span class="mat-font-body-sm">{{ pass.label }}</span>
          <mat-icon class="mat-icon-sm" [class]="pass.railClass" aria-hidden="true">{{ pass.railIcon }}</mat-icon>
        </div>
      }
    }
  }

  <!-- 3. Detail slot: element stays mounted (fixed min-height) so the card height never jumps,
       but the surface tint only renders with content — an empty slot is an invisible spacer.
       Waiting reason > live attempt line > engine liveness; empty until one applies. -->
  <div
    class="plan-progress-card__detail mat-corner-sm d-flex align-items-start gap-2 px-2"
    [class.mat-bg-surface-container-low]="detailText()"
  >
    @if (detailText(); as detail) {
      <mat-icon class="mat-icon-sm mat-text-on-surface-variant flex-shrink-0" aria-hidden="true">info_outline</mat-icon>
      <span class="mat-font-label-sm mat-text-on-surface-variant">{{ detail }}</span>
    }
  </div>

  <!-- 4. Planned tasks: pre-engine intent list — LIVE tasks list first, plan intents fallback,
       task ids in the row tooltips; "—" placeholder until the planning tasks list arrives.
       Hidden once the execution board below renders. -->
  @if (boardSteps().length === 0) {
    <span class="mat-font-label-sm mat-text-on-surface-variant text-uppercase">Planned tasks</span>
    @for (task of plannedRows(); track task.task_id) {
      <div class="d-flex align-items-start gap-2" [matTooltip]="task.task_id">
        <mat-icon class="mat-icon-sm tone-pri flex-shrink-0" aria-hidden="true">checklist</mat-icon>
        <span class="mat-font-body-sm">{{ task.text }}</span>
      </div>
    } @empty {
      <div class="d-flex align-items-start gap-2">
        <mat-icon class="mat-icon-sm mat-text-on-surface-variant flex-shrink-0" aria-hidden="true">checklist</mat-icon>
        <span class="mat-font-body-sm mat-text-on-surface-variant">—</span>
      </div>
    }
  }

  <!-- 5. Execution: grouped task board shared with the plan card, without the confidence badge. -->
  @if (boardSteps().length > 0) {
    <span class="mat-font-label-sm mat-text-on-surface-variant text-uppercase">Tasks</span>
    <app-plan-step-list [steps]="boardSteps()" [showConfidence]="false" />
  }
</div>